// InteliChat #41 - "never miss a chat" composable (the ALERTING layer).
//
// Wraps the pure urgency buckets (./useUrgencyBuckets) and adds the
// always-on HUD's side concerns: per-agent alert prefs, conversation
// navigation, and bootstrapping the singleton alertEngine so its single
// ActionCable-bus subscription drives sound/notification/favicon off the
// exact same ground truth. Nothing is polled and nothing is fabricated -
// absent data reads as empty. The bucket membership + urgency logic live
// in useUrgencyBuckets + ./urgency (single source of truth).
import { computed, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useInteliConversation } from 'dashboard/intelichat/composables/useInteliConversation';
import { useUrgencyBuckets } from './useUrgencyBuckets';
import engine from './alertEngine';

const DEFAULTS = {
  soundOn: true,
  volume: 0.8,
  tone: 'ding',
  notificationsOn: true,
};

export function useNeverMiss() {
  const route = useRoute();
  const router = useRouter();
  const { uiSettings, updateUISettings } = useUISettings();
  const currentUser = useMapGetter('getCurrentUser');

  const {
    openConversations,
    mineCount,
    contactName,
    isUnassigned,
    lastMessage,
  } = useInteliConversation();

  // the reactive urgency buckets (pure; owns presence + the live clock)
  const buckets = useUrgencyBuckets();
  const {
    now,
    needsYouNow,
    newIncoming,
    missed,
    needsYouNowCount,
    newCount,
    missedCount,
    actNow,
    topUrgent,
    actNowCount,
  } = buckets;

  const lastLineOf = chat => {
    const m = lastMessage(chat);
    const c = m && m.content != null ? String(m.content) : '';
    return c.slice(0, 80) || 'New conversation';
  };

  // ---- per-agent prefs (persisted via native uiSettings) ------------
  const prefs = computed(() => ({
    soundOn: uiSettings.value?.intelichat_alert_sound ?? DEFAULTS.soundOn,
    volume: uiSettings.value?.intelichat_alert_volume ?? DEFAULTS.volume,
    tone: uiSettings.value?.intelichat_alert_tone ?? DEFAULTS.tone,
    notificationsOn:
      uiSettings.value?.intelichat_alert_notifications ?? DEFAULTS.notificationsOn,
  }));

  const setPref = (key, value) => {
    const map = {
      soundOn: 'intelichat_alert_sound',
      volume: 'intelichat_alert_volume',
      tone: 'intelichat_alert_tone',
      notificationsOn: 'intelichat_alert_notifications',
    };
    if (map[key]) updateUISettings({ [map[key]]: value });
  };

  const toggleSound = () => {
    const next = !prefs.value.soundOn;
    setPref('soundOn', next);
    if (!next) engine.stopRepeat(); // silence any active loop immediately
  };

  // ---- open conversation (route) + navigation -----------------------
  const openId = computed(() => {
    const id = Number(route.params?.conversationId);
    return Number.isFinite(id) && id > 0 ? id : null;
  });

  const navigate = id => {
    if (!id) return;
    router.push({
      name: 'intelichat_inbox',
      params: { accountId: route.params.accountId, conversationId: id },
    });
    engine.stopRepeat();
  };

  // ---- engine context snapshot (read live on each bus event) --------
  const getConversation = (id, payload) => {
    const chat = openConversations.value.find(c => Number(c.id) === Number(id));
    if (chat) {
      return {
        id: chat.id,
        name: contactName(chat),
        lastLine: lastLineOf(chat),
        isUnassigned: isUnassigned(chat),
        isNew: isUnassigned(chat),
      };
    }
    return payload
      ? {
          id,
          name: payload?.meta?.sender?.name || payload?.sender?.name || 'Customer',
          lastLine: (payload?.content || 'sent a message').toString().slice(0, 80),
          isUnassigned: !payload?.meta?.assignee,
          isNew: !payload?.meta?.assignee,
        }
      : null;
  };

  const getContext = () => ({
    prefs: prefs.value,
    openId: openId.value,
    currentUserId: currentUser.value?.id || null,
    newCount: newCount.value,
    getConversation,
    navigate,
  });

  // ---- bootstrap + reactive wiring ----------------------------------
  onMounted(() => {
    engine.bootstrap(getContext);
    engine.setCounters(actNowCount.value);
  });

  // passive channels (favicon count / title) track the ACT-NOW total
  // (NEEDS YOU NOW + NEW), never MISSED - spec's rail/badge rule.
  watch(actNowCount, n => engine.setCounters(n));

  // opening/answering a conversation kills any repeat loop
  watch(openId, id => {
    if (id) engine.stopRepeat();
  });

  return {
    // clock (shared so rows + hero live-count off one timer)
    now,
    // buckets (spec order: needsYouNow -> new -> missed)
    needsYouNow,
    newIncoming,
    missed,
    needsYouNowCount,
    newCount,
    missedCount,
    // act-now roll-up for the Today hero + rail badge
    actNow,
    topUrgent,
    actNowCount,
    mineCount,
    // prefs
    prefs,
    setPref,
    toggleSound,
    // actions
    navigate,
    engine,
    // notification permission passthrough (gesture-driven)
    notificationPermission: () => engine.notificationPermission(),
    requestNotificationPermission: () => engine.requestNotificationPermission(),
  };
}
